import styled from '@emotion/styled';
import React from 'react';

import { ToastManager } from '../../ToastManager';
import { IconButton } from '../buttons/IconButton';

import { useI18n } from '../../hooks/useI18nFormatters';

import { su } from '../../theme/functions/su.ts';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

interface Props {
  value?: string;
  message?: string;
  autoFocus?: boolean;
  keyedAutoFocus?: string;
  styleColor?: 'blue' | 'light';
  styleTextAlign?: 'center' | 'left' | 'right';
}

export function CellClipboard(props: Props) {
  const { i18n } = useI18n();

  const iconButtonColor = (() => {
    if (props.styleColor === 'light') {
      return theme.color.icon_light;
    }

    return theme.color.blue_500;
  })();

  return (
    <S.Root data-style-color={props.styleColor} data-style-text-align={props.styleTextAlign}>
      <S.Input
        style={{ '--clipboard-text-align': props.styleTextAlign }}
        data-style-color={props.styleColor}
        value={props.value}
        readOnly={true}
      />

      <IconButton
        autoFocus={props.autoFocus}
        keyedAutoFocus={props.keyedAutoFocus}
        title={i18n.messageFormatter('common.copy')}
        url={glyph.core.squareOnSquare}
        color={iconButtonColor}
        onPress={() => {
          if (props.value == null) return;

          navigator.clipboard
            .writeText(props.value)
            .then(() => {
              // @ts-expect-error -- not typed yet
              ToastManager.add({
                icon: glyph.core.squareOnSquare,
                message: props.message ?? i18n.messageFormatter('common.copied'),
              });
            })
            .catch(ToastManager.handleError);
        }}
      />
    </S.Root>
  );
}

function S() {}
CellClipboard.S = S;
CellClipboard.IconButton = IconButton;

S.Root = styled.div`
  position: relative;
  display: flex;
  gap: ${su(1)};
`;

interface CSSProperties extends React.CSSProperties {
  '--clipboard-text-align'?: string;
}

S.Input = styled.input<{ style: CSSProperties }>`
  width: 100%;
  height: ${su(5)};
  padding: ${su(1)} 0;
  background-color: ${theme.color.blue_050};
  color: ${theme.color.blue_500};
  border: 1px solid ${theme.color.blue_500};
  border-radius: ${theme.borderRadius.small};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: var(--clipboard-text-align, center);

  &:focus {
    text-overflow: clip;
  }

  &[data-style-color='light'] {
    background-color: unset;
    color: ${theme.color.text_light};
    border: 0;
    border-radius: 0;
  }
`;
